import { imgBaseUrl } from "../../api/api";

/**
 * Progress report cards — ONE design used for both on-screen preview and print.
 *
 *  - Every card carries its own school header (logo, name, address) so that
 *    each card can be cut out and handed to a student.
 *  - Cards are grouped N per A4 page (1, 2, 4, 6, 8); a card never splits
 *    across pages. Dashed lines between cards act as cutting guides.
 *
 * Exports:
 *   buildProgressCardsHtml(opts) -> html string (pages)
 *   getProgressCardsCss(perPage)  -> css string (scoped under .pcp)
 *   printProgressCards(opts)      -> opens print window
 *   PROGRESS_PER_PAGE_OPTIONS
 *
 * opts = { students, testTitle, batchTitle, sessionName, perPage }
 */

export const PROGRESS_PER_PAGE_OPTIONS = [1, 2, 4];

export const DEFAULT_SHOW = {
  rank: true,
  classTeacher: true,
  parent: true,
  principal: true,
  signatureImg: false,
};

const esc = (val) =>
  String(val ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );

// grid shape + density per "cards per page"
const LAYOUTS = {
  1: {
    cols: 1,
    rows: 1,
    font: 14,
    pad: 7,
    logo: 22,
    address: true,
    sign: true,
    signGap: 16,
  },
  2: {
    cols: 1,
    rows: 2,
    font: 12,
    pad: 5,
    logo: 18,
    address: true,
    sign: true,
    signGap: 12,
  },
  4: {
    cols: 2,
    rows: 2,
    font: 10.5,
    pad: 4,
    logo: 14,
    address: true,
    sign: true,
    signGap: 12,
  },
};
const getLayout = (perPage) => LAYOUTS[perPage] || LAYOUTS[4];

const getSetting = () => {
  try {
    return JSON.parse(localStorage.getItem("setting") ?? "{}") || {};
  } catch {
    return {};
  }
};

const percent = (student) => {
  const total = Number(student?.total_marks || 0);
  if (!total) return "0.0";
  return ((Number(student?.obtained_total || 0) / total) * 100).toFixed(1);
};

const fmtDob = (v) => {
  if (!v) return "—";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : String(v);
};

/* ---------------------------------------------------------------- CARD */

const buildCard = (student, ctx) => {
  const { setting, testTitle, batchTitle, sessionName, layout, show } = ctx;
  const d = student?.student_details || {};

  const logo = setting?.logo_img ? imgBaseUrl + setting.logo_img : "";
  const signatureUrl = setting?.signature ? imgBaseUrl + setting.signature : "";
  const address = [setting?.village, setting?.district, setting?.state]
    .filter(Boolean)
    .join(", ");

  const signs = [
    show.classTeacher && { label: "Class Teacher" },
    show.parent && { label: "Parent" },
    show.principal && {
      label: "Principal",
      img: show.signatureImg ? signatureUrl : "",
    },
  ].filter(Boolean);

  const rows = (student?.subject_details || [])
    .map(
      (s) => `
        <tr>
          <td class="l">${esc(s?.name)}</td>
          <td>${esc(s?.subject_marks)}</td>
          <td><b>${s?.obtained === "A" ? "Absent" : esc(s?.obtained ?? "-")}</b></td>
        </tr>`,
    )
    .join("");

  return `
    <div class="cell">
      <div class="card">

        <div class="school">
          ${logo ? `<img class="logo" src="${esc(logo)}" alt="" />` : `<div class="logo"></div>`}
          <div class="school-text">
            <h1>${esc(setting?.name || "School Name")}</h1>
            ${
              layout.address && address
                ? `<p>${esc(address)}${setting?.pin_code ? " - " + esc(setting.pin_code) : ""}</p>`
                : ""
            }
          </div>
          <div class="logo"></div>
        </div>

        <div class="ribbon">
          <span class="pill">PROGRESS REPORT</span>
          <span class="ribbon-text">${esc(testTitle || "")}${
            sessionName ? ` &nbsp;|&nbsp; ${esc(sessionName)}` : ""
          }</span>
        </div>

        <div class="info">
  <div class="wide"><span>Student Name</span><b>${esc(d?.name || "—")}</b></div>
  <div><span>Father's Name</span><b>${esc(d?.father_name || "—")}</b></div>
  <div><span>Mother's Name</span><b>${esc(d?.mother_name || "—")}</b></div>
  <div><span>Reg. No.</span><b>${esc(d?.registration_no || "—")}</b></div>
  <div><span>Date of Birth</span><b>${esc(fmtDob(d?.dob))}</b></div>
  <div class="wide"><span>Batch</span><b>${esc(batchTitle || "—")}</b></div>
</div>

        <table>
          <thead>
            <tr><th class="l">Subject</th><th>Max Marks</th><th>Obtained</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>

        <div class="foot${show.rank ? "" : " no-rank"}">
          <div><span>Percentage</span><b>${percent(student)}%</b></div>
          ${
            show.rank
              ? `<div class="c"><span>Rank</span><b>${esc(student?.rank || "—")}</b></div>`
              : ""
          }
          <div class="r"><span>Total</span><b>${esc(student?.obtained_total ?? "-")} / ${esc(student?.total_marks ?? "-")}</b></div>
        </div>

        ${
          layout.sign && signs.length
            ? `<div class="sign">
                 ${signs
                   .map(
                     (s) => `<div class="${s.img ? "has-img" : ""}">
                       ${s.img ? `<img class="sig-img" src="${esc(s.img)}" alt="" />` : ""}
                       <i></i>${s.label}
                     </div>`,
                   )
                   .join("")}
               </div>`
            : ""
        }
      </div>
    </div>`;
};

/* ---------------------------------------------------------------- HTML */

export const buildProgressCardsHtml = ({
  students = [],
  testTitle = "",
  batchTitle = "",
  sessionName = "",
  perPage = 4,
  show = DEFAULT_SHOW,
}) => {
  const layout = getLayout(perPage);
  const setting = getSetting();
  const ctx = {
    setting,
    testTitle,
    batchTitle,
    sessionName,
    layout,
    show: { ...DEFAULT_SHOW, ...show },
  };

  const pages = [];
  for (let i = 0; i < students.length; i += perPage) {
    pages.push(students.slice(i, i + perPage));
  }

  return pages
    .map(
      (chunk) => `
      <section class="page">
        <div class="grid">${chunk.map((s) => buildCard(s, ctx)).join("")}</div>
      </section>`,
    )
    .join("");
};

/* ----------------------------------------------------------------- CSS */

export const getProgressCardsCss = (perPage = 4) => {
  const { cols, rows, font, pad, logo, signGap } = getLayout(perPage);
  const P = ".pcp";

  return `
  ${P}, ${P} * { box-sizing: border-box; }
  ${P} {
    font-family: Arial, Helvetica, sans-serif; color: #111;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  ${P} .page {
    width: 194mm; height: 280mm; background: #fff;
    page-break-after: always; break-after: page; overflow: hidden;
  }
  ${P} .page:last-child { page-break-after: auto; break-after: auto; }

  ${P} .grid {
    width: 100%; height: 100%;
    display: grid;
    grid-template-columns: repeat(${cols}, minmax(0, 1fr));
    grid-template-rows: repeat(${rows}, minmax(0, 1fr));
  }

  /* dashed cell border = cutting guide */
  ${P} .cell {
    padding: 2mm; min-width: 0; min-height: 0;
    border: 1px dashed #9aa0ad;
    margin: -0.5px;
  }

  ${P} .card {
    height: 100%; display: flex; flex-direction: column;
    border: 1.5px solid #16233f; border-radius: 4px; overflow: hidden;
    font-size: ${font}px; background: #fff; color: #111;
  }

  /* school header — on every card */
  ${P} .school {
    display: grid; grid-template-columns: ${logo}mm 1fr ${logo}mm;
    align-items: center; gap: ${pad + 2}px;
    padding: ${pad + 1}px ${pad + 4}px;
    border-bottom: 2px solid #16233f;
  }
  ${P} .logo { width: ${logo}mm; height: ${logo}mm; object-fit: contain; }
  ${P} .school-text { text-align: center; min-width: 0; }
  ${P} .school-text h1 {
    margin: 0; font-size: ${font + 3.5}px; line-height: 1.15;
    letter-spacing: .4px; text-transform: uppercase; color: #16233f;
  }
  ${P} .school-text p {
    margin: 2px 0 0; font-size: ${font - 1.5}px; color: #444; line-height: 1.2;
  }

  ${P} .ribbon {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: ${pad}px ${pad + 4}px; background: #16233f; color: #fff;
  }
  ${P} .pill {
    border: 1px solid #fff; border-radius: 20px; padding: 0 8px;
    font-size: ${font - 1.5}px; font-weight: 700; letter-spacing: 1px; white-space: nowrap;
  }
  ${P} .ribbon-text { font-weight: 600; font-size: ${font}px; }

  ${P} .info {
    display: grid; grid-template-columns: 1fr 1fr;
    border-bottom: 1px solid #16233f;
  }
  ${P} .info > div {
    padding: ${pad}px ${pad + 4}px; min-width: 0;
    border-bottom: 1px solid #d5d9e2;
  }
  ${P} .info > div:nth-child(odd):not(.wide) { border-left: 1px solid #d5d9e2; }
  ${P} .info > div:last-child { border-bottom: 0; }
  ${P} .info > div.wide { grid-column: 1 / -1; border-left: 0; }
  ${P} .info span, ${P} .foot span {
    display: block; font-size: ${font - 2.5}px; color: #555;
    text-transform: uppercase; letter-spacing: .3px;
  }
  ${P} .info b {
    display: block; font-size: ${font}px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  ${P} .info .wide b { font-size: ${font + 1.5}px; text-transform: uppercase; }

  ${P} table { width: 100%; border-collapse: collapse; }
  ${P} th { background: #e8ecf4; font-size: ${font - 0.5}px; }
  ${P} th, ${P} td {
    padding: ${pad}px ${pad + 4}px; text-align: center;
    border-bottom: 1px solid #c9cfdb;
  }
  ${P} th.l, ${P} td.l { text-align: left; }
  ${P} tbody tr:nth-child(even) td { background: #f6f8fb; }

  ${P} .foot {
    margin-top: auto; display: grid; grid-template-columns: 1fr 1fr 1fr;
    padding: ${pad + 1}px ${pad + 4}px;
    background: #e8ecf4; border-top: 1px solid #16233f;
  }
  ${P} .foot b { font-size: ${font + 2}px; color: #16233f; }
  ${P} .foot .c { text-align: center; }
  ${P} .foot .r { text-align: right; }
  ${P} .foot.no-rank { grid-template-columns: 1fr 1fr; }

  ${P} .sign {
    display: flex; gap: 10px;
    padding: ${signGap}mm ${pad + 6}px ${pad + 2}px;
    font-size: ${font - 2}px; color: #333; text-align: center;
  }
  ${P} .sign > div { flex: 1; }
  ${P} .sign i { display: block; border-top: 1px solid #111; margin-bottom: 2px; }
  ${P} .sign > div.has-img { position: relative; }
  ${P} .sign .sig-img {
    position: absolute; left: 50%; bottom: 100%;
    transform: translateX(-50%);
    max-width: 90%; max-height: ${signGap - 1}mm;
    object-fit: contain;
  }
  `;
};

/* --------------------------------------------------------------- PRINT */

export const printProgressCards = (opts) => {
  const { students = [], testTitle = "", perPage = 4 } = opts || {};

  if (!students.length) {
    alert("No students to print");
    return;
  }

  const win = window.open("", "_blank");
  if (!win) {
    alert("Popup blocked! Please allow popups to print.");
    return;
  }

  win.document.write(`<!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${esc(testTitle || "Progress Report")}</title>
        <style>
          @page { size: A4 portrait; margin: 8mm; }
          html, body { margin: 0; padding: 0; background: #fff; }
          ${getProgressCardsCss(perPage)}
        </style>
      </head>
      <body>
        <div class="pcp">${buildProgressCardsHtml(opts)}</div>
      </body>
    </html>`);
  win.document.close();

  // wait for logos before printing
  const imgs = Array.from(win.document.images || []);
  Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) return resolve();
          img.addEventListener("load", resolve, { once: true });
          img.addEventListener("error", resolve, { once: true });
        }),
    ),
  ).then(() => {
    setTimeout(() => {
      win.focus();
      win.print();
      win.close();
    }, 250);
  });
};
